import { z } from 'zod';
import {
    EMAIL_MAX_LENGTH,
    EMAIL_MESSAGE,
    EMAIL_PATTERN,
    HTTPS_URL_MESSAGE,
    HTTPS_URL_PATTERN,
    IMEI_MESSAGE,
    IMEI_PATTERN,
    PHONE_MX_MESSAGE,
    PHONE_MX_PATTERN,
    RFC_MESSAGE,
    RFC_PATTERN,
    SKU_MAX_LENGTH,
    SKU_MESSAGE,
    SKU_PATTERN,
    ZIP_CODE_MX_MESSAGE,
    ZIP_CODE_MX_PATTERN,
} from '@fiado/type-kit/bin/common/constants/InputPatterns';
import { isValidImei } from '@fiado/type-kit/bin/common/helpers/imeiChecksum';

/**
 * Validadores de campos reutilizables para los formularios (react-hook-form + zod).
 * Fuente única de las reglas (formato + longitud + mensajes en es-MX). Importar estos
 * campos en los schemas en vez de re-declarar `z.string()` suelto en cada form.
 */

/** Normaliza un teléfono a solo dígitos, conservando un `+` inicial (E.164). */
export function normalizePhone(value: string): string {
    const trimmed = value.trim();
    const hasPlus = trimmed.startsWith('+');
    const digits = trimmed.replace(/\D/g, '');
    return hasPlus ? `+${digits}` : digits;
}

/**
 * ¿Teléfono válido? Lenient a propósito para NO bloquear la edición de usuarios con teléfonos
 * legacy: acepta 7–15 dígitos, con `+` opcional (E.164). Ignora espacios, guiones, paréntesis y
 * puntos. Rechaza lo obviamente inválido (letras, muy corto). El ideal en México son 10 dígitos.
 */
export function isValidPhone(value: string): boolean {
    return /^\+?\d{7,15}$/.test(normalizePhone(value));
}

const PHONE_MESSAGE = 'Teléfono inválido: usa solo dígitos (10 en México), o +código de país.';

/** Nombre requerido (1–80 caracteres). */
export const requiredNameField = z
    .string()
    .trim()
    .min(1, 'El nombre es requerido')
    .max(80, 'Máximo 80 caracteres');

/** Nombre opcional (hasta 80 caracteres) — para perfil propio. */
export const optionalNameField = z.string().trim().max(80, 'Máximo 80 caracteres').optional();

/** Teléfono opcional; si se ingresa, debe ser válido. Vacío se acepta. */
export const optionalPhoneField = z
    .string()
    .trim()
    .max(20, 'Teléfono demasiado largo')
    .refine((v) => v === '' || isValidPhone(v), PHONE_MESSAGE)
    .optional();

/** Un string requerido genérico con límite de longitud y mensajes en español. */
export function requiredString(label = 'Este campo', max = 120): z.ZodString {
    return z
        .string()
        .trim()
        .min(1, `${label} es requerido`)
        .max(max, `Máximo ${String(max)} caracteres`);
}

/** Separadores que la máscara o un pegado dejan en un teléfono. */
const PHONE_SEPARATORS = /[\s\-().]/g;

const phoneDigits = z.string().transform((value) => value.replace(PHONE_SEPARATORS, ''));

/** Teléfono MX requerido: exactamente 10 dígitos; sale sin separadores. */
export function mxPhoneField(requiredMessage = 'Escribe el teléfono.') {
    return phoneDigits.pipe(
        z.string().min(1, requiredMessage).regex(PHONE_MX_PATTERN, PHONE_MX_MESSAGE),
    );
}

/** Teléfono MX opcional: vacío o 10 dígitos; sale sin separadores. */
export const optionalMxPhoneField = phoneDigits.pipe(
    z.string().refine((value) => value === '' || PHONE_MX_PATTERN.test(value), PHONE_MX_MESSAGE),
);

/** Correo requerido; sale recortado y en minúsculas. */
export function emailField(requiredMessage = 'Escribe el correo.') {
    return z
        .string()
        .trim()
        .toLowerCase()
        .min(1, requiredMessage)
        .max(EMAIL_MAX_LENGTH, `El correo admite hasta ${String(EMAIL_MAX_LENGTH)} caracteres.`)
        .regex(EMAIL_PATTERN, EMAIL_MESSAGE);
}

/** RFC requerido; sale recortado y en mayúsculas (12 persona moral, 13 física). */
export function rfcField(requiredMessage = 'Escribe el RFC.') {
    return z.string().trim().toUpperCase().min(1, requiredMessage).regex(RFC_PATTERN, RFC_MESSAGE);
}

/** Código postal MX requerido: 5 dígitos. */
export function mxZipCodeField(requiredMessage = 'Escribe el código postal.') {
    return z
        .string()
        .trim()
        .min(1, requiredMessage)
        .regex(ZIP_CODE_MX_PATTERN, ZIP_CODE_MX_MESSAGE);
}

/** SKU requerido: letras, números, guiones y espacios entre palabras; hasta 64. */
export function skuField(requiredMessage = 'Escribe el SKU.') {
    return z
        .string()
        .trim()
        .min(1, requiredMessage)
        .max(SKU_MAX_LENGTH, SKU_MESSAGE)
        .regex(SKU_PATTERN, SKU_MESSAGE);
}

export const IMEI_CHECKSUM_MESSAGE = 'El IMEI no es válido: revisa los 15 dígitos.';

/** IMEI requerido: 15 dígitos y dígito verificador Luhn. */
export function imeiField(requiredMessage = 'Escribe el IMEI.') {
    return z
        .string()
        .trim()
        .min(1, requiredMessage)
        .regex(IMEI_PATTERN, IMEI_MESSAGE)
        .refine(isValidImei, IMEI_CHECKSUM_MESSAGE);
}

export { isValidImei };

const CLABE_WEIGHTS = [3, 7, 1] as const;

/**
 * CLABE interbancaria: 18 dígitos donde el último es el verificador de Banxico. Cada uno de los
 * 17 primeros se multiplica por 3, 7, 1 (en ciclo), se suma el módulo 10 de cada producto y el
 * verificador es `(10 - suma mod 10) mod 10`.
 */
export function isValidClabe(value: string): boolean {
    if (!/^\d{18}$/.test(value)) return false;
    let sum = 0;
    for (let index = 0; index < 17; index += 1) {
        sum += (Number(value[index]) * CLABE_WEIGHTS[index % 3]) % 10;
    }
    return (10 - (sum % 10)) % 10 === Number(value[17]);
}

/** URL opcional: vacía o que empiece con https://. */
export const optionalHttpsUrlField = z
    .string()
    .trim()
    .refine((value) => value === '' || HTTPS_URL_PATTERN.test(value), HTTPS_URL_MESSAGE);

/** El primer mensaje de error de `schema` para `value`, o `null` si es válido. */
export function fieldError(schema: z.ZodType, value: unknown): string | null {
    const result = schema.safeParse(value);
    return result.success ? null : (result.error.issues[0]?.message ?? null);
}

export type FieldErrors<K extends string> = Partial<Record<K, string>>;

/** Valida un form sin react-hook-form: los datos ya normalizados, o el primer error por campo. */
export function validateForm<T extends z.ZodTypeAny>(
    schema: T,
    values: unknown,
):
    | { success: true; data: z.output<T> }
    | { success: false; errors: FieldErrors<Extract<keyof z.input<T>, string>> } {
    const result = schema.safeParse(values);
    if (result.success) return { success: true, data: result.data as z.output<T> };
    const errors: Record<string, string> = {};
    for (const issue of result.error.issues) {
        const key = String(issue.path[0] ?? '');
        if (key !== '' && !(key in errors)) errors[key] = issue.message;
    }
    return { success: false, errors };
}
